/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import autobind from 'class-autobind';
import { fetchJSON } from '../../support/Fetch';
import ThumbnailImage from './ThumbnailImage';
import {
  getFileURL,
  toPreviewURL,
  isDocument,
  isImage,
} from '../../support/helpers/Files';

import { FileDesc } from '../../types/shared/FileDesc';

type ResponseData = {
  error?: string;
  retry_after?: number;
  url: string;
};

type Props = {
  onPreview?: (file: FileDesc, event: Event) => any;
  file?: FileDesc;
  width?: number;
  height?: number;
};

type State = {
  loadError: boolean;
  showError: string | null | undefined;
  imageURL: string | null | undefined;
};

export default class Thumbnail extends Component<Props, State> {
  _isMounted: boolean;
  _fetchingID: string | null | undefined;

  constructor(props: Props) {
    super(props);
    autobind(this);
    this.state = {
      loadError: false,
      showError: null,
      imageURL: null,
    };
    this._isMounted = true; // Pretty much
  }

  componentDidMount() {
    this._updateThumbnail(this.props.file);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: Props) {
    let { file } = this.props;
    let oldID = prevProps.file ? prevProps.file.id : null;
    let newID = file ? file.id : null;

    if (oldID !== newID) {
      this._updateThumbnail(file);
    }
  }

  render() {
    let { width, height, file } = this.props;
    let { showError, imageURL } = this.state;
    return (
      <ThumbnailImage
        width={width}
        height={height}
        showError={showError}
        href={file ? getFileURL(file) : undefined}
        src={imageURL}
        onError={this._onLoadError}
        onClick={this._onClick}
      />
    );
  }

  _onLoadError() {
    this.setState({ loadError: true });
  }

  _onClick(event: Event) {
    event.preventDefault();
    let { onPreview, file } = this.props;
    let { loadError, imageURL } = this.state;
    // Ensure we've successfully generated a preview image before
    // attempting to call onPreview().
    if (imageURL && !loadError && onPreview && file) {
      onPreview(file, event);
    }
  }

  _updateThumbnail(file: FileDesc | null | undefined) {
    this.setState({
      loadError: false,
      showError: null,
      imageURL: null,
    });
    // Note: If `file` exists then `id` field should always be present, but for
    // some rare cases in our DB it is not. I believe the problem is now fixed
    // so no new entries will get into the db without a valid `id` but adding
    // this second condition here until we clean what's in the DB.
    if (!file || typeof file.id !== 'string') {
      return;
    }
    // The preview field is for when the image hasn't yet been sent to the
    // server but it can sometimes be present after the upload is complete, so
    // we also check the "_" prefix to determine if this is a local-only image.
    // TODO: We should probably not use this Thumbnail component for provisional
    // image uploads
    if (file.id.startsWith('_') && isImage(file) && file.preview) {
      this.setState({ imageURL: file.preview });
      return;
    }
    if (isImage(file)) {
      this.setState({ imageURL: getFileURL(file, { size: 'thumbnail' }) });
    } else if (isDocument(file)) {
      this._fetchPreviewURL(file);
    } else {
      this.setState({ showError: t('Invalid File Type') });
    }
  }

  _fetchPreviewURL(file: FileDesc) {
    let { id } = file;
    this._fetchingID = id;
    let url = `/api/files/preview/${id}`;
    // TODO: Use cancellable version of fetchJSON
    fetchJSON(url, (error: Error | null | undefined, data: any) => {
      // Ensure that we've not unmounted or changed to a different image.
      if (this._fetchingID === file.id && this._isMounted) {
        this._onFetchResponse(file, error, data);
      }
    });
  }

  _onFetchResponse(
    file: FileDesc,
    error: Error | null | undefined,
    data: ResponseData,
  ) {
    if (error) {
      this.setState({ showError: t('Loading Error') });
    } else if (data.error) {
      this.setState({ showError: data.error });
    } else if (data.retry_after) {
      setTimeout(() => {
        if (this._fetchingID === file.id && this._isMounted) {
          this._fetchPreviewURL(file);
        }
      }, data.retry_after * 1000);
    } else {
      this._fetchingID = null;
      this.setState({ imageURL: toPreviewURL(data.url) });
    }
  }
}
